import React, { useEffect, useState } from 'react'
import { ChainConfiguration } from '@shared/DeploymentConfigTypes'
import { fetchSwapEventsForTokenPair } from '@react/fetchers/swapEventFetchers'
import { useOtChainState } from '@react/redux/store'
import { getSwapTokenConfig } from '@shared/SwapTokenConfig'
import axios from 'axios'
import { logOT } from '@shared/loggingShared'

interface Live24hVolumeProps {
  serverApiBaseUrl?: string
  restAPIKey?: string
  poolAddr?: string
  chainConfiguration: ChainConfiguration
}

export function Live24hVolume({ serverApiBaseUrl, restAPIKey, poolAddr, chainConfiguration }: Live24hVolumeProps) {
  const [volume, setVolume] = useState<number | null>(null)
  const { stageConfiguration } = useOtChainState()

  async function fetchVolumeFromOKX() {
    if (!serverApiBaseUrl || !restAPIKey || !chainConfiguration) return 0

    try {
      const chainId = Number(chainConfiguration.chainId)
      const stageName = stageConfiguration.stageName

      logOT.info('Fetching 24h volume from OKX API', { stageName, chainId })

      const response = await axios.get(`${serverApiBaseUrl}/okx/volume`, {
        headers: {
          'x-api-key': restAPIKey,
          'Content-Type': 'application/json',
        },
        params: {
          stageName,
          chainId,
        },
        timeout: 10000,
      })

      if (response.data && typeof response.data.volume24h === 'number') {
        logOT.info('Successfully fetched volume from OKX:', response.data.volume24h)
        return response.data.volume24h
      } else {
        logOT.warn('Invalid response from OKX API:', response.data)
        return 0
      }
    } catch (error) {
      logOT.error('Error fetching volume from OKX API:', error)
      return fetchVolumeLegacy()
    }
  }

  async function fetchVolumeLegacy() {
    if (!serverApiBaseUrl || !restAPIKey || !chainConfiguration) return 0

    try {
      const chainId = Number(chainConfiguration.chainId)
      const swapConfig = getSwapTokenConfig(stageConfiguration.stageName, chainId)
      const tokenA = swapConfig.TOKEN_A.address
      const tokenB = swapConfig.TOKEN_B.address

      const swapEvents = await fetchSwapEventsForTokenPair(tokenA, tokenB, serverApiBaseUrl, restAPIKey, chainId)

      const now = Date.now()
      const oneDayAgo = now - 24 * 60 * 60 * 1000

      const recentSwaps = swapEvents.filter((event) => event.timestamp >= oneDayAgo)

      const total = recentSwaps.reduce((sum, event) => {
        const volume = parseFloat(event.volume) || 0
        return sum + volume
      }, 0)

      return total
    } catch (error) {
      logOT.error('Error in legacy volume calculation:', error)
      return 0
    }
  }

  async function fetchVolume() {
    return fetchVolumeFromOKX()
  }

  useEffect(() => {
    const cached = localStorage.getItem('lyt_24h_volume')
    const cachedTime = localStorage.getItem('lyt_24h_volume_time')
    if (cached && cachedTime) {
      setVolume(Number(cached))
    }
  }, [])

  // Fetch if needed on mount or when dependencies change
  useEffect(() => {
    const cached = localStorage.getItem('lyt_24h_volume')
    const cachedTime = localStorage.getItem('lyt_24h_volume_time')
    const now = Date.now()
    if (cached && cachedTime && now - Number(cachedTime) < 30 * 60 * 1000) {
      setVolume(Number(cached))
    } else {
      fetchVolume().then((v) => {
        setVolume(v)
        localStorage.setItem('lyt_24h_volume', String(v))
        localStorage.setItem('lyt_24h_volume_time', String(now))
      })
    }
  }, [serverApiBaseUrl, restAPIKey, poolAddr])

  // Refresh every hour on the hour
  useEffect(() => {
    const msUntilNextHour = () => {
      const now = new Date()
      return (60 - now.getMinutes()) * 60 * 1000 - now.getSeconds() * 1000 - now.getMilliseconds()
    }

    let timeout: NodeJS.Timeout
    let interval: NodeJS.Timeout

    function scheduleNextFetch() {
      timeout = setTimeout(() => {
        fetchVolume().then((v) => {
          setVolume(v)
          localStorage.setItem('lyt_24h_volume', String(v))
          localStorage.setItem('lyt_24h_volume_time', String(Date.now()))
        })
        interval = setInterval(
          () => {
            fetchVolume().then((v) => {
              setVolume(v)
              localStorage.setItem('lyt_24h_volume', String(v))
              localStorage.setItem('lyt_24h_volume_time', String(Date.now()))
            })
          },
          30 * 60 * 1000
        )
      }, msUntilNextHour())
    }

    scheduleNextFetch()

    return () => {
      if (timeout) clearTimeout(timeout)
      if (interval) clearInterval(interval)
    }
  }, [serverApiBaseUrl, restAPIKey, poolAddr])

  const formatted =
    volume !== null
      ? `$${volume.toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })}`
      : 'Loading...'
  return <>{formatted}</>
}
